import React from "react";
import { ShieldCheck, HardHat, MapPin, Zap } from "lucide-react";
import SectionLabel from "./brand/SectionLabel";

const FEATURES = [
  {
    icon: HardHat,
    title: "Hands-On Trade",
    body: "Towers, conduit, cabling, coring, mounting — we bring the right gear and know how to use it.",
  },
  {
    icon: ShieldCheck,
    title: "Certified & Insured",
    body: "Fully insured operation with tower-climbing certifications and electrical safety compliance.",
  },
  {
    icon: MapPin,
    title: "Rural-Smart",
    body: "We live and work in the country too — we understand acreage, farm and off-grid realities.",
  },
  {
    icon: Zap,
    title: "Integrated Stack",
    body: "One contractor from RF design → network → power → antennas → sign-off and support.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-bg-primary border-t border-steel-dark overflow-hidden"
    >
      {/* lattice backdrop */}
      <div className="absolute inset-0 bg-lattice-grid bg-lattice opacity-30 pointer-events-none" />
      <div className="absolute inset-0 bg-signal-radial pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-6 lg:col-span-5">
            <SectionLabel className="mb-5">Who We Are</SectionLabel>
            <h2 className="heading text-4xl md:text-5xl lg:text-6xl text-white uppercase leading-[0.95] mb-8">
              Contractor <br />
              Built For <br />
              <span className="text-signal">The Prairies.</span>
            </h2>
            <p className="text-steel-light text-base md:text-lg leading-relaxed mb-6">
              Epicom Systems Ltd. is a Southern Alberta-based communications
              contractor covering tower builds, wireless infrastructure,
              business and home networking, and cell booster / DAS system
              design &amp; installation. Whether it's a single access point on
              an acreage or a multi-tower PtMP rollout for a solar project, we
              run the full job end-to-end.
            </p>
            <p className="text-steel-light text-base md:text-lg leading-relaxed mb-8">
              We're based in the prairies and will travel for the right jobs.
              If you've got a site with no signal, a farm that needs
              connectivity, or a commercial build that needs a proper enterprise-grade
              backbone — we're the call.
            </p>

            <div className="flex flex-wrap gap-3">
              <span className="font-mono text-xs tracking-[0.2em] text-signal border border-signal/50 px-4 py-2 uppercase">
                Southern Alberta Based
              </span>
              <span className="font-mono text-xs tracking-[0.2em] text-white border border-steel-dark px-4 py-2 uppercase">
                Willing to Travel
              </span>
              <span className="font-mono text-xs tracking-[0.2em] text-white border border-steel-dark px-4 py-2 uppercase">
                Fully Insured
              </span>
            </div>
          </div>

          <div className="md:col-span-6 lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-[1px] bg-steel-dark border border-steel-dark">
              {FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.title}
                    data-testid={`about-feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="bg-bg-secondary p-8 hover:bg-[#16181B] transition-colors group"
                  >
                    <Icon
                      className="text-signal mb-5 group-hover:scale-110 transition-transform origin-left"
                      size={28}
                      strokeWidth={1.5}
                    />
                    <h3 className="heading text-xl text-white uppercase mb-2">
                      {f.title}
                    </h3>
                    <p className="text-steel-light text-sm leading-relaxed">
                      {f.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
